import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useDataCache } from "../context/DataCacheContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  FileSpreadsheet, Loader2, Search, Save, ArrowLeft,
  FileText, Receipt, Banknote, CheckCircle,
  Calculator, TrendingUp, TrendingDown, Download, ChevronDown, Eye,
  Merge, AlertTriangle, Trash2, ArrowUpDown
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config/api";
import { normalizeForSearch, getPaymentStatus } from "../utils/helpers";
import { EtapaFinanciaraForm } from "../components/evidence/EtapaFinanciaraForm";


const ETAPE_CONFIG = [
  { key: "faza_prejudiciara", label: "Fază Prejudiciară", color: "bg-purple-500" },
  { key: "fond", label: "Fond", color: "bg-blue-500" },
  { key: "apel", label: "Apel", color: "bg-orange-500" },
  { key: "executare_silita", label: "Executare Silită", color: "bg-red-500" },
];


export default function EvidențaBCPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchEvidenceBC: fetchCachedEvidenceBC, invalidateCache } = useDataCache();
  const [evidenta, setEvidenta] = useState(null);
  const [allEvidenta, setAllEvidenta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState(null);
  
  // Merge state
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [targetEvidenceId, setTargetEvidenceId] = useState(null);

  // Sort & Filter state
  const [sortBy, setSortBy] = useState("intern"); // "intern" or "restant"
  const [statusFilter, setStatusFilter] = useState("toate"); // "toate", "incasat", "partial", "neincasat"

  const caseIdFromUrl = searchParams.get("caseId");
  const isAdmin = user?.role === "admin";

  const fetchAllEvidenta = useCallback(async (forceRefresh = false) => {
    try {
      const data = await fetchCachedEvidenceBC(forceRefresh);
      setAllEvidenta(data);
    } catch (error) {
      console.error("Error fetching evidenta BC:", error);
      toast.error("Eroare la încărcarea evidențelor");
    } finally {
      setLoading(false);
    }
  }, [fetchCachedEvidenceBC]);

  // Global totals across all records
  const globalTotals = useMemo(() => {
    let totalFacturat = 0, totalIncasat = 0, totalRestant = 0, partialCount = 0;
    allEvidenta.forEach(r => {
      totalFacturat += r.total_facturi || 0;
      totalIncasat += r.total_incasat || 0;
      totalRestant += r.total_neincasat || 0;
      if (getPaymentStatus(r) === "partial") partialCount++;
    });
    return { totalFacturat, totalIncasat, totalRestant, partialCount };
  }, [allEvidenta]);

  // Filter locally instead of API calls
  const filteredEvidenta = useMemo(() => {
    let result = [...allEvidenta];
    
    if (searchQuery.trim()) {
      const query = normalizeForSearch(searchQuery);
      result = result.filter(e => 
        normalizeForSearch(e.numar_dosar).includes(query) ||
        normalizeForSearch(e.numar_dosar_intern).includes(query) ||
        normalizeForSearch(e.nume_client).includes(query) ||
        normalizeForSearch(e.nume_cedent).includes(query)
      );
    }
    
    // Filter by payment status
    if (statusFilter !== "toate") {
      result = result.filter(r => {
        const status = getPaymentStatus(r);
        if (statusFilter === "incasat") return status === "incasat";
        if (statusFilter === "partial") return status === "partial";
        if (statusFilter === "neincasat") return status === "neincasat" || status === "fara_date";
        return true;
      });
    }
    
    // Sort
    if (sortBy === "restant") {
      result.sort((a, b) => (b.total_neincasat || 0) - (a.total_neincasat || 0));
    } else {
      result.sort((a, b) => {
        const internA = a.numar_dosar_intern || "";
        const internB = b.numar_dosar_intern || "";
        const numA = parseInt(internA.split('/')[0], 10);
        const numB = parseInt(internB.split('/')[0], 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        if (!isNaN(numA)) return -1;
        if (!isNaN(numB)) return 1;
        return internA.localeCompare(internB, 'ro');
      });
    }
    
    return result;
  }, [allEvidenta, searchQuery, sortBy, statusFilter]);

  const fetchEvidențaByCase = useCallback(async (caseId) => {
    try {
      const response = await axios.get(
        `${API}/evidenta-bc/by-case/${caseId}`,
        getAuthHeader()
      );
      setEvidenta(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error("Error fetching evidenta BC:", error);
      toast.error("Eroare la încărcarea evidenței");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (caseIdFromUrl) {
      fetchEvidențaByCase(caseIdFromUrl);
    } else {
      fetchAllEvidenta();
    }
  }, [caseIdFromUrl]);

  const handleSave = async () => {
    if (!formData) return;
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/evidenta-bc/${formData.id}`,
        {
          observatii: formData.observatii,
          faza_prejudiciara: formData.faza_prejudiciara,
          fond: formData.fond,
          apel: formData.apel,
          executare_silita: formData.executare_silita
        },
        getAuthHeader()
      );
      toast.success("Evidența BC a fost salvată");
      setEvidenta(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error("Error saving evidenta:", error);
      toast.error("Eroare la salvare: " + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEtapaChange = (etapaKey, data) => {
    setFormData(prev => ({
      ...prev,
      [etapaKey]: data
    }));
  };

  // Calculate totals from formData/evidenta for the colored summary cards
  const calculatedTotals = useMemo(() => {
    const source = formData || evidenta;
    if (!source) return { totalFacturi: 0, totalPlati: 0, totalIncasat: 0, totalNeincasat: 0 };
    
    const etapeKeys = ["faza_prejudiciara", "fond", "apel", "executare_silita"];
    
    let totalFacturi = 0;
    let totalPlati = 0;
    let totalIncasat = 0;
    
    etapeKeys.forEach(key => {
      const etapa = source[key] || {};
      // Legacy single factura
      totalFacturi += parseFloat(etapa.factura_valoare) || 0;
      // Multiple facturi
      (etapa.facturi || []).forEach(f => {
        totalFacturi += parseFloat(f.valoare) || 0;
      });
      // Legacy single chitanta (plată)
      totalPlati += parseFloat(etapa.chitanta_valoare) || 0;
      // Multiple chitante (plăți)
      (etapa.chitante || []).forEach(c => {
        totalPlati += parseFloat(c.valoare) || 0;
      });
      // Suma incasata efectiv - aceasta este "Total Încasat"
      totalIncasat += parseFloat(etapa.suma_incasata_efectiv) || 0;
    });
    
    // Total neîncasat = Total Facturat - Total Încasat
    const totalNeincasat = totalFacturi - totalIncasat;
    
    return { totalFacturi, totalPlati, totalIncasat, totalNeincasat: totalNeincasat > 0 ? totalNeincasat : 0 };
  }, [formData, evidenta]);

  const handleExportJson = async () => {
    try {
      const response = await axios.get(`${API}/evidenta-bc/export/json`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Export_Evidenta_BC_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export JSON realizat cu succes!");
    } catch (error) {
      toast.error("Eroare la exportul JSON");
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/evidenta-bc/export/excel`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Export_Evidenta_BC_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export Excel realizat cu succes!");
    } catch (error) {
      toast.error("Eroare la exportul Excel");
    }
  };

  // Merge functions
  const handleToggleMergeMode = () => {
    setMergeMode(!mergeMode);
    setSelectedForMerge([]);
    setTargetEvidenceId(null);
  };

  const handleToggleSelectForMerge = (recordId) => {
    setSelectedForMerge(prev => {
      if (prev.includes(recordId)) {
        return prev.filter(id => id !== recordId);
      }
      if (prev.length < 2) {
        return [...prev, recordId];
      }
      return prev;
    });
  };

  const handleOpenMergeDialog = () => {
    if (selectedForMerge.length === 2) {
      setTargetEvidenceId(selectedForMerge[1]);
    }
    setMergeDialogOpen(true);
  };

  const handleMerge = async () => {
    if (selectedForMerge.length !== 2 || !targetEvidenceId) return;
    
    setMerging(true);
    try {
      const sourceId = selectedForMerge.find(id => id !== targetEvidenceId);
      await axios.post(`${API}/evidenta-bc/merge`, {
        source_id: sourceId,
        target_id: targetEvidenceId
      }, getAuthHeader());
      
      toast.success("Evidențele BC au fost combinate cu succes");
      setMergeDialogOpen(false);
      setSelectedForMerge([]);
      setTargetEvidenceId(null);
      setMergeMode(false);
      invalidateCache('evidenceBC');
      await fetchAllEvidenta(true);
    } catch (error) {
      console.error("Merge error:", error);
      toast.error(error.response?.data?.detail || "Eroare la combinare");
    } finally {
      setMerging(false);
    }
  };

  const getSelectedMergeRecords = () => {
    return selectedForMerge.map(id => allEvidenta.find(r => r.id === id)).filter(Boolean);
  };

  const handleDeleteEvidenta = async (evidenta_id, numar_dosar) => {
    if (!window.confirm(`Sigur doriți să ștergeți evidența BC pentru dosarul ${numar_dosar || 'N/A'}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/evidenta-bc/${evidenta_id}`, getAuthHeader());
      toast.success("Evidența BC a fost ștearsă");
      invalidateCache('evidenceBC');
      await fetchAllEvidenta(true);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Eroare la ștergere");
    }
  };

  const isColaboratorExtern = user?.position === "colaborator_extern";
  const canEdit = isAdmin && !isColaboratorExtern;

  // If viewing a specific case's evidenta
  if (caseIdFromUrl && evidenta) {
    return (
      <div className="space-y-6" data-testid="evidenta-bc-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate("/evidenta-bc")}
              className="mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Înapoi la Evidență BC
            </Button>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6" />
              Evidență BC
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Dosar: {evidenta.numar_dosar_intern} | {evidenta.numar_dosar}
            </p>
          </div>
          
          {canEdit && (
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Save className="w-4 h-4 mr-2" />
                Salvează Modificările
              </Button>
            </div>
          )}
        </div>

        {/* General Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Informații Generale</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Nr. Intern</Label>
                <p className="font-mono font-semibold">{evidenta.numar_dosar_intern}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Nr. Dosar</Label>
                <p className="font-semibold">{evidenta.numar_dosar}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Client</Label>
                <p className="font-semibold">{evidenta.nume_client || "-"}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Cedent Creanță</Label>
                <p className="font-semibold text-amber-700">{evidenta.nume_cedent || "-"}</p>
              </div>
            </div>
            
            <div className="mt-4">
              <Label className="text-xs text-muted-foreground">Observații</Label>
              {canEdit ? (
                <Textarea
                  value={formData?.observatii || ""}
                  onChange={(e) => setFormData({ ...formData, observatii: e.target.value })}
                  placeholder="Adaugă observații..."
                  rows={2}
                  className="mt-1"
                />
              ) : (
                <p className="text-sm">{evidenta.observatii || "-"}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Totals Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-blue-700">
                <Receipt className="w-4 h-4" />
                <span className="text-xs font-medium">Total Facturi</span>
              </div>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {calculatedTotals.totalFacturi.toFixed(2)} RON
              </p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-purple-700">
                <Banknote className="w-4 h-4" />
                <span className="text-xs font-medium">Total Plăți</span>
              </div>
              <p className="text-2xl font-bold text-purple-900 mt-1">
                {calculatedTotals.totalPlati.toFixed(2)} RON
              </p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-green-700">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-medium">Total Încasat</span>
              </div>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {calculatedTotals.totalIncasat.toFixed(2)} RON
              </p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-red-700">
                <TrendingDown className="w-4 h-4" />
                <span className="text-xs font-medium">Total Neîncasat</span>
              </div>
              <p className="text-2xl font-bold text-red-900 mt-1">
                {calculatedTotals.totalNeincasat.toFixed(2)} RON
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Etape Procesuale */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Etape Procesuale</CardTitle>
            <CardDescription>
              Evidența financiară pentru fiecare etapă a dosarului
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="faza_prejudiciara" className="w-full">
              <TabsList className="grid grid-cols-4 w-full">
                {ETAPE_CONFIG.map(etapa => (
                  <TabsTrigger key={etapa.key} value={etapa.key} className="text-xs sm:text-sm">
                    {etapa.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {ETAPE_CONFIG.map(etapa => (
                <TabsContent key={etapa.key} value={etapa.key}>
                  <EtapaFinanciaraForm
                    etapa={etapa.key}
                    data={formData?.[etapa.key] || evidenta[etapa.key] || {}}
                    onChange={(data) => handleEtapaChange(etapa.key, data)}
                    disabled={!canEdit}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6" data-testid="evidenta-bc-list">
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6" />
            Evidență BC
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Evidența financiară pentru dosarele de recuperare creanțe
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && !mergeMode && allEvidenta.length >= 2 && (
            <Button
              variant="outline"
              onClick={handleToggleMergeMode}
              data-testid="start-merge-bc-btn"
            >
              <Merge className="w-4 h-4 mr-2" />
              Combină Evidențe
            </Button>
          )}
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-dropdown-evidenta-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Exportă
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportJson} data-testid="export-json-evidenta">
                  <FileText className="w-4 h-4 mr-2" />
                  Export JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportExcel} data-testid="export-excel-evidenta">
                  <FileText className="w-4 h-4 mr-2" />
                  Export Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/export/evidenta-bc-report`, {
                      ...getAuthHeader(),
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `evidenta_bc_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("PDF exportat");
                  } catch (error) {
                    toast.error("Eroare la export PDF");
                  }
                }} data-testid="export-pdf-evidenta-bc">
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* 5. Global Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="global-summary-bar-bc">
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Total Facturat</p>
          <p className="text-xl font-bold text-blue-800 mt-1 tabular-nums">{globalTotals.totalFacturat.toLocaleString('ro-RO', {minimumFractionDigits: 2})} RON</p>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Total Încasat</p>
          <p className="text-xl font-bold text-green-800 mt-1 tabular-nums">{globalTotals.totalIncasat.toLocaleString('ro-RO', {minimumFractionDigits: 2})} RON</p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-xs font-medium text-red-600 uppercase tracking-wide">Total Restant</p>
          <p className="text-xl font-bold text-red-800 mt-1 tabular-nums">{globalTotals.totalRestant.toLocaleString('ro-RO', {minimumFractionDigits: 2})} RON</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Parțial Încasate</p>
          <p className="text-xl font-bold text-amber-800 mt-1">{globalTotals.partialCount} dosare</p>
        </div>
      </div>

      {/* Merge Mode Banner */}
      {mergeMode && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-800">
                <Merge className="w-5 h-5" />
                <span className="font-medium">
                  Mod Combinare: Selectați 2 evidențe pentru a le combina
                </span>
                <Badge variant="outline" className="ml-2 border-amber-500 text-amber-700">
                  {selectedForMerge.length}/2 selectate
                </Badge>
              </div>
              <div className="flex gap-2">
                {selectedForMerge.length === 2 && (
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700"
                    onClick={handleOpenMergeDialog}
                    data-testid="confirm-merge-bc-btn"
                  >
                    <Merge className="w-4 h-4 mr-2" />
                    Combină Selectate
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToggleMergeMode}
                  data-testid="cancel-merge-bc-btn"
                >
                  Anulează
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3+4. Toolbar: Search + Sort + Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Caută după număr dosar, client sau cedent..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-evidenta-bc"
          />
        </div>
        
        {/* Sort buttons */}
        <Button
          variant={sortBy === "intern" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("intern")}
          data-testid="sort-intern-bc-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          Nr. Intern
        </Button>
        <Button
          variant={sortBy === "restant" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("restant")}
          data-testid="sort-restant-bc-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          Sold Restant
        </Button>
        
        <div className="h-6 w-px bg-border" />
        
        {/* Filter buttons */}
        {[
          { key: "toate", label: "Toate" },
          { key: "incasat", label: "Încasate" },
          { key: "partial", label: "Parțial" },
          { key: "neincasat", label: "Neîncasate" },
        ].map(f => (
          <Button
            key={f.key}
            variant={statusFilter === f.key ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(f.key)}
            className={statusFilter === f.key && f.key === "neincasat" ? "bg-red-600 hover:bg-red-700" : 
                        statusFilter === f.key && f.key === "partial" ? "bg-amber-600 hover:bg-amber-700" :
                        statusFilter === f.key && f.key === "incasat" ? "bg-green-600 hover:bg-green-700" : ""}
            data-testid={`filter-${f.key}-bc-btn`}
          >
            {f.label}
          </Button>
        ))}
      </div>
      </div>{/* End Sticky */}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : filteredEvidenta.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{allEvidenta.length === 0 ? "Nu există evidențe BC" : "Nu s-au găsit rezultate"}</p>
              {allEvidenta.length === 0 && (
                <p className="text-sm mt-2">Creați un dosar BC pentru a genera automat evidența</p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {mergeMode && <TableHead className="w-12"></TableHead>}
                  <TableHead>Nr. Intern</TableHead>
                  <TableHead>Nr. Dosar</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Cedent</TableHead>
                  <TableHead className="text-right">Facturat</TableHead>
                  <TableHead className="text-right">Încasat</TableHead>
                  <TableHead className="text-right">Restant</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvidenta.map((item) => {
                  const isSelected = selectedForMerge.includes(item.id);
                  const status = getPaymentStatus(item);
                  const borderColor = status === "incasat" ? "border-l-green-500" :
                                      status === "partial" ? "border-l-amber-500" :
                                      status === "neincasat" ? "border-l-red-500" : "border-l-gray-200";
                  return (
                    <TableRow 
                      key={item.id}
                      className={`cursor-pointer hover:bg-muted/50 border-l-4 ${borderColor} ${isSelected ? 'bg-amber-50' : ''}`}
                      onClick={() => {
                        if (mergeMode) {
                          handleToggleSelectForMerge(item.id);
                        } else if (item.case_bc_id) {
                          navigate(`/evidenta-bc?caseId=${item.case_bc_id}`);
                        }
                      }}
                    >
                      {mergeMode && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleToggleSelectForMerge(item.id)}
                            disabled={!isSelected && selectedForMerge.length >= 2}
                            data-testid={`merge-checkbox-bc-${item.id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-mono font-medium">
                        {item.numar_dosar_intern}
                      </TableCell>
                      <TableCell>{item.numar_dosar}</TableCell>
                      <TableCell>{item.nume_client || "-"}</TableCell>
                      <TableCell className="text-amber-700 font-medium">
                        {item.nume_cedent || "-"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {(item.total_facturi || 0).toLocaleString('ro-RO', {minimumFractionDigits: 2})}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium text-green-700">
                        {(item.total_incasat || 0).toLocaleString('ro-RO', {minimumFractionDigits: 2})}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium text-red-700">
                        {(item.total_neincasat || 0).toLocaleString('ro-RO', {minimumFractionDigits: 2})}
                      </TableCell>
                      <TableCell>
                        {status === "incasat" && <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Încasat</Badge>}
                        {status === "partial" && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Parțial</Badge>}
                        {status === "neincasat" && <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Neîncasat</Badge>}
                        {status === "fara_date" && <Badge variant="outline" className="text-gray-500">-</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.case_bc_id) {
                                navigate(`/evidenta-bc?caseId=${item.case_bc_id}`);
                              }
                            }}
                            title="Vezi detalii"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteEvidenta(item.id, item.numar_dosar);
                              }}
                              title="Șterge evidența"
                              data-testid={`delete-evidenta-bc-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      {allEvidenta.length > 0 && (
        <div className="flex items-center gap-5 px-4 py-3 bg-muted/40 rounded-lg text-sm text-muted-foreground">
          <div className="flex items-center gap-2"><div className="w-3 h-1 rounded bg-green-500" /> Complet încasat</div>
          <div className="flex items-center gap-2"><div className="w-3 h-1 rounded bg-amber-500" /> Parțial încasat</div>
          <div className="flex items-center gap-2"><div className="w-3 h-1 rounded bg-red-500" /> Neîncasat</div>
          <span className="ml-auto text-xs">Bordura colorată din stânga fiecărui rând = indicator vizual rapid</span>
        </div>
      )}

      {/* Merge Confirmation Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Confirmă Combinarea Evidențelor BC
            </DialogTitle>
            <DialogDescription>
              Datele din evidența ștearsă vor fi combinate în evidența păstrată. Această acțiune este ireversibilă.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Selectați evidența care va fi <strong>păstrată</strong>:
            </p>
            
            {getSelectedMergeRecords().map((record) => (
              <div
                key={record.id}
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  targetEvidenceId === record.id
                    ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setTargetEvidenceId(record.id)}
                data-testid={`select-target-bc-${record.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{record.numar_dosar_intern}</p>
                    <p className="text-sm text-muted-foreground">{record.numar_dosar}</p>
                    <p className="text-sm">{record.nume_client || "-"}</p>
                  </div>
                  <div className="text-right">
                    {targetEvidenceId === record.id ? (
                      <Badge className="bg-green-500">Va fi păstrată</Badge>
                    ) : (
                      <Badge variant="destructive">Va fi ștearsă</Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>
              Anulează
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleMerge}
              disabled={merging || !targetEvidenceId}
              data-testid="execute-merge-bc-btn"
            >
              {merging && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Merge className="w-4 h-4 mr-2" />
              Combină Evidențele
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
